import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Input } from '../components/ui/atoms/Input';
import { Label } from '../components/ui/label';
import { Button } from '@/components/ui/atoms/Button';
import { AuthLayout } from '../components/auth/AuthLayout';
import { requestPasswordReset } from '../services/auth.service';

export const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const VALUE_PROPS = [
    { text: t('auth:forgot.value_prop_1') },
    { text: t('auth:forgot.value_prop_2') },
    { text: t('auth:forgot.value_prop_3') },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !email.trim()) return;
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      planTitle={t('auth:forgot.plan_title')}
      planTagline={t('auth:forgot.plan_tagline')}
      planFeatures={VALUE_PROPS}
      description={t('auth:forgot.plan_description')}
    >
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-dark-invirtus">
            {t('auth:forgot.title')}
          </h1>
          <p className="mt-1 text-sm text-dark-invirtus/60">
            {t('auth:forgot.subtitle')}
          </p>
        </div>

        {done ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-light-lila bg-light-lin/40 p-4">
              <p className="text-sm text-dark-invirtus">
                {t('auth:forgot.check_inbox', { email })}
              </p>
            </div>
            <Link
              to="/login"
              className="block text-center text-sm font-medium text-purple hover:underline"
            >
              {t('auth:forgot.back_to_sign_in')}
            </Link>
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="forgot-email" className="text-dark-invirtus">
                {t('common:forms.email')}
              </Label>
              <Input
                id="forgot-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('common:forms.email_placeholder')}
                rounded="lg"
              />
            </div>

            <Button
              label={submitting ? t('auth:forgot.sending') : t('auth:forgot.send_link')}
              type="contained"
              intent="secondary"
              htmlType="submit"
              disabled={submitting}
              loading={submitting}
              className="w-full"
            />

            <p className="text-center text-sm text-dark-invirtus/60">
              <Link to="/login" className="font-medium text-purple hover:underline">
                {t('auth:forgot.back_to_sign_in')}
              </Link>
            </p>
          </form>
        )}
      </div>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
